import {announce} from '@github-ui/aria-live'
import {human} from '@github-ui/formatters'
import {useEffect, useState} from 'react'

import {getBlankMessage, getResultsAnnouncement} from './components/ListMessage'
import {PickerDialog} from './components/PickerDialog'
import {PickerHeaderContent} from './components/PickerHeaderContent'
import {SelectAllRow} from './components/SelectAllRow'
import {concatAndDedup} from './helper/concat-and-dedup'
import {useQuerySearch} from './hooks/use-query-search'
import {PickerListBody} from './PickerListBody'
import type {IPickerItem, SelectPickerDialogProps} from './types'

export function SelectPickerDialog<T extends IPickerItem>({
  selected,
  onChange,
  onSubmit,
  onCancel: onCancelProp,
  onDismiss,
  returnFocusRef,
  selectionVariant,
  title,
  description,
  providers,
  itemConfig,
  onRenderFooterDetails,
  pageSize = 100,
  maxVisibleSelectedItems = 500,
  ...restProps
}: SelectPickerDialogProps<T>) {
  const {getSearchUrl, literals, initialQuery = '', emptyQueryMessage, ...extraConfig} = {...itemConfig, ...restProps}

  const [query, setQuery] = useState(initialQuery)

  const [selectedItems, setSelectedItems] = useState(selected || [])
  const [snapshotSelectedItems, setSnapshotSelectedItems] = useState(selectedItems)
  const skipFirstPageResults = !query && snapshotSelectedItems.length > pageSize
  const skipEmptyQuery = !!emptyQueryMessage && !query
  const skipQuery = skipEmptyQuery || skipFirstPageResults

  const results = useQuerySearch<T>({searchUrl: getSearchUrl(query), enabled: !skipQuery})
  const onApply = () => {
    onSubmit(selectedItems)
    onDismiss()
  }

  const onCancel = () => {
    onChange?.(selected || [])
    onCancelProp?.()
    onDismiss()
  }

  const select = (items: T[]) => {
    setSelectedItems(items)
    onChange?.(items)
  }

  const fetchedItems = results.data?.items || []
  const materializedVisibleItems = snapshotSelectedItems.filter(item => !extraConfig.isHollow(item))
  const visibleItems = concatAndDedup(materializedVisibleItems, fetchedItems, pageSize, maxVisibleSelectedItems)
  const blankMessage = skipEmptyQuery ? emptyQueryMessage : getBlankMessage(results, visibleItems, literals)
  const visibleIds = new Set(visibleItems.map(item => item.id))
  const [visibleSelectedItems, hiddenSelectedItems] = partitionArray(selectedItems, item => visibleIds.has(item.id))

  const totalItemsCount = results.data?.totalCount || 0
  const resultsMessage = getResultsAnnouncement({
    blankMessage,
    itemsCount: visibleItems.length,
    totalItemsCount,
    literals,
  })

  useEffect(() => {
    if (!results.isLoading && resultsMessage) {
      announce(resultsMessage)
    }
  }, [results.isLoading, resultsMessage])

  const onQueryChanged = (newQuery: string) => {
    setQuery(newQuery)
    setSnapshotSelectedItems(newQuery ? [] : selectedItems)
  }

  return (
    <PickerDialog
      onClose={onCancel}
      footerButtons={[
        {
          onClick: onCancel,
          content: 'Cancel',
        },
        {
          buttonType: 'primary',
          onClick: onApply,
          content: 'Select',
          count: selectionVariant === 'multiple' && selectedItems.length > 0 ? human(selectedItems.length) : undefined,
        },
      ]}
      onSubmit={onApply}
      returnFocusRef={returnFocusRef}
      renderHeader={({dialogLabelId, dialogDescriptionId}) => (
        <div>
          <PickerHeaderContent
            dialogTitle={title}
            dialogLabelId={dialogLabelId}
            dialogDescription={description}
            dialogDescriptionId={dialogDescriptionId}
            inputLabel={`Filter ${literals.itemsName}`}
            initialQuery={initialQuery}
            onDismiss={onDismiss}
            onQueryExecuted={onQueryChanged}
            providers={providers}
          />
          {selectionVariant === 'multiple' && (
            <SelectAllRow
              itemsCount={visibleItems.length}
              selectedCount={visibleSelectedItems.length}
              onSelectAll={() => select(hiddenSelectedItems.concat(visibleItems))}
              onSelectNone={() => select(hiddenSelectedItems)}
            />
          )}
        </div>
      )}
      renderBody={() => (
        <PickerDialog.Body>
          <PickerListBody
            hiddenSelectedCount={hiddenSelectedItems.length}
            isFiltered={!!query}
            blankMessage={blankMessage}
            totalItemsCount={totalItemsCount}
            items={visibleItems}
            selectionVariant={selectionVariant}
            selectedItems={selectedItems}
            setSelectedItems={select}
            title={literals.listTitle}
            {...extraConfig}
          />
        </PickerDialog.Body>
      )}
      onRenderFooterDetails={onRenderFooterDetails}
    />
  )
}

function partitionArray<T extends IPickerItem>(array: T[], predicate: (item: T) => boolean) {
  return array.reduce<[T[], T[]]>(
    (pairOfArrays, item) => {
      const targetArray = predicate(item) ? pairOfArrays[0] : pairOfArrays[1]
      targetArray.push(item)
      return pairOfArrays
    },
    [[], []],
  )
}
